import { Box } from '@mui/material';
import { AxisBottom, AxisRight } from '@visx/axis';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { useParentSize } from '@visx/responsive';
import { scaleLinear, scaleTime } from '@visx/scale';
import { Bar, LinePath } from '@visx/shape';
import { Threshold } from '@visx/threshold';
import { withTooltip } from '@visx/tooltip';
import { bisector, extent, max, min } from 'd3-array';
import { timeFormat } from 'd3-time-format';
import { useCallback, useId, useMemo } from 'react';
import { roundNum } from '../../../helpers';
import ChartTooltip from './ChartTooltip';

const formatMonth = timeFormat('%b');
const bisectDate = bisector(d => d.dateObj).left;

const formatAxisValue = (value) => {
  const absValue = Math.abs(value);
  if (absValue >= 1e12) return (value / 1e12) + 'T';
  if (absValue >= 1e9) return (value / 1e9) + 'B';
  if (absValue >= 1e6) return (value / 1e6) + 'M';
  if (absValue >= 1e3) return (value / 1e3) + 'K';
  return value;
};

const calculateFontSize = (width, height) => {
  const baseFontSize = Math.min(width, height) / 50;
  return Math.max(11, Math.min(baseFontSize, 16));
};

const Chart = withTooltip(({
  data,
  showTooltip,
  hideTooltip,
  tooltipData,
  tooltipTop,
  tooltipLeft,
  _height = 300,
  _margin = { top: 20, right: 50, bottom: 50, left: 20 },
  threshold = [0],
  isRSI = false,
}) => {
  const idBase = useId();
  const { parentRef, width } = useParentSize({ debounceTime: 150 });

  const sortedData = useMemo(() => {
    if (!data?.series_data?.length) return null;
    const rows = data.series_data
      .filter(d => d != null && d.value != null && !Number.isNaN(d.value))
      .map(d => ({ ...d, dateObj: new Date(d.date) }))
      .filter(d => !Number.isNaN(d.dateObj.getTime()))
      .sort((a, b) => a.dateObj - b.dateObj);
    return rows.length ? rows : null;
  }, [data]);

  const dimensions = useMemo(() => {
    const height = _height;
    const margin = _margin;
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    return { width, height, margin, innerWidth, innerHeight };
  }, [width, _height, _margin.top, _margin.right, _margin.bottom, _margin.left]);

  const hasNegativeValues = useMemo(() => {
    if (!sortedData) return false;
    return sortedData.some(d => d.value < 0);
  }, [sortedData]);

  const { xScale, yScale } = useMemo(() => {
    if (!sortedData) return { xScale: null, yScale: null };

    const xScale = scaleTime({
      range: [dimensions.margin.left, dimensions.width - dimensions.margin.right],
      domain: extent(sortedData, d => d.dateObj),
    });

    const yScale = scaleLinear({
      range: [dimensions.innerHeight + dimensions.margin.top, dimensions.margin.top],
      domain: [min(sortedData, d => d.value) ?? 0, max(sortedData, d => d.value) ?? 1],
      nice: true,
    });

    return { xScale, yScale };
  }, [sortedData, dimensions]);

  const handleTooltip = useCallback(
    (event) => {
      if (!xScale || !yScale || !sortedData?.length) return;

      const { x } = localPoint(event) || { x: 0 };
      const x0 = xScale.invert(x);
      const index = bisectDate(sortedData, x0, 1);
      const d0 = sortedData[index - 1];
      const d1 = sortedData[index];
      let d = d0;
      if (d0 && d1) {
        d = x0 - d0.dateObj > d1.dateObj - x0 ? d1 : d0;
      }
      if (!d) return;

      showTooltip({
        tooltipData: d,
        tooltipLeft: xScale(d.dateObj),
        tooltipTop: yScale(d.value),
      });
    },
    [xScale, yScale, sortedData, showTooltip]
  );

  const dynamicFontSize = calculateFontSize(dimensions.width, dimensions.height);

  if (!sortedData) return null;

  const renderThresholds = () => {
    if (isRSI) {
      return (
        <>
          <Threshold
            id={`${idBase}-rsi-low`}
            data={sortedData}
            x={d => roundNum(xScale(d.dateObj), 1)}
            y0={d => roundNum(yScale(d.value), 1)}
            y1={d => yScale(30)}
            clipAboveTo={0}
            clipBelowTo={dimensions.innerHeight + 20}
            belowAreaProps={{
              fill: '#006600',
              fillOpacity: 0.4,
            }}
            aboveAreaProps={{
              fill: 'transparent',
              fillOpacity: 0,
            }}
          />
          <Threshold
            id={`${idBase}-rsi-high`}
            data={sortedData}
            x={d => roundNum(xScale(d.dateObj), 1)}
            y0={d => roundNum(yScale(d.value), 1)}
            y1={d => yScale(70)}
            clipAboveTo={0}
            clipBelowTo={dimensions.innerHeight}
            belowAreaProps={{
              fill: '#174c86',
              fillOpacity: 0.1,
            }}
            aboveAreaProps={{
              fill: '#990000',
              fillOpacity: 0.6,
            }}
          />
        </>
      );
    }
    return (
      <Threshold
        id={`${idBase}-main`}
        data={sortedData}
        x={d => roundNum(xScale(d.dateObj), 1)}
        y0={d => roundNum(yScale(d.value), 1)}
        y1={hasNegativeValues ? d => yScale(0) : roundNum(dimensions.height - dimensions.margin.bottom)}
        clipAboveTo={0}
        clipBelowTo={dimensions.innerHeight}
        belowAreaProps={{
          fill: `url(#${idBase}-negative-gradient)`,
          fillOpacity: hasNegativeValues ? 1 : 0,
        }}
        aboveAreaProps={{
          fill: `url(#${idBase}-positive-gradient)`,
          fillOpacity: 1,
        }}
      />
    );
  };

  const renderThresholdLines = () => {
    return threshold.map(th => (
      <LinePath
        key={`threshold-line-${th}`}
        data={sortedData}
        x={d => roundNum(xScale(d.dateObj), 1)}
        y={() => roundNum(yScale(th), 1)}
        stroke={isRSI && th === 30 ? "#006600" : isRSI && th === 70 ? "#990000" : "#333"}
        strokeWidth={1}
        strokeOpacity={0.5}
        strokeDasharray="4,4"
      />
    ));
  };

  return (
    <Box component="div" ref={parentRef} sx={{ height: _height, width: '100%', position: 'relative' }}>
      {width > 50 && dimensions.innerHeight > 0 && xScale && yScale && (
        <svg
          width={width}
          height={dimensions.height}
          viewBox={`0 0 ${width} ${dimensions.height}`}
          role="img"
          aria-label="Time series chart with threshold areas"
        >
          <defs>
            <linearGradient id={`${idBase}-positive-gradient`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#174C86" stopOpacity={0.38} />
              <stop offset="100%" stopColor="#e6f4ea" stopOpacity={0.38} />
            </linearGradient>
            <linearGradient id={`${idBase}-negative-gradient`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#174C86" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#174C86" stopOpacity={0.3} />
            </linearGradient>
          </defs>
          <rect
            x={0}
            y={0}
            width={width}
            height={dimensions.height}
            fill={'#fff'}
          />
          <Group>
            {renderThresholds()}
            <LinePath
              data={sortedData}
              x={d => roundNum(xScale(d.dateObj), 1)}
              y={d => roundNum(yScale(d.value), 1)}
              stroke="#174C86"
              strokeWidth={1}
            />
            {renderThresholdLines()}
            <AxisBottom
              top={dimensions.height - dimensions.margin.bottom}
              scale={xScale}
              tickFormat={(date) => {
                const month = date.getMonth();
                const year = date.getFullYear();
                return month === 0 ? `${year}` : formatMonth(date);
              }}
              stroke="transparent"
              tickStroke="transparent"
              tickLength={0}
              numTicks={width > 370 ? 7 : 5}
              tickComponent={({ x, y, formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  x={x}
                  y={y}
                  fill="#333"
                  dy=".57em"
                  fontSize={`${dynamicFontSize}px`}
                  textAnchor="middle"
                >
                  {formattedValue}
                </text>
              )}
            />

            <AxisRight
              left={dimensions.innerWidth + dimensions.margin.left}
              scale={yScale}
              tickFormat={formatAxisValue}
              stroke="transparent"
              tickStroke="transparent"
              tickLength={0}
              numTicks={width > 370 ? 7 : 5}
              tickComponent={({ x, y, formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  x={x + 5}
                  y={y}
                  fill="#333"
                  dy=".32em"
                  textAnchor="start"
                  fontSize={`${dynamicFontSize}px`}
                >
                  {formattedValue}
                </text>
              )}
            />

            <Bar
              x={dimensions.margin.left}
              y={dimensions.margin.top}
              width={Math.max(0, dimensions.innerWidth)}
              height={Math.max(0, dimensions.innerHeight)}
              fill="transparent"
              onTouchStart={handleTooltip}
              onTouchMove={handleTooltip}
              onMouseMove={handleTooltip}
              onMouseLeave={hideTooltip}
            />

            {tooltipData && (
              <g>
                <line
                  x1={tooltipLeft}
                  x2={tooltipLeft}
                  y1={dimensions.margin.top}
                  y2={dimensions.innerHeight + dimensions.margin.top}
                  stroke="#77777770"
                  strokeWidth={1}
                  pointerEvents="none"
                  strokeDasharray="5,2"
                />
                <circle
                  cx={tooltipLeft}
                  cy={tooltipTop}
                  r={4}
                  fill="#174C86"
                  stroke="white"
                  strokeWidth={1}
                  pointerEvents="none"
                />
              </g>
            )}
          </Group>
        </svg>
      )}
      <ChartTooltip
        tooltipData={tooltipData}
        tooltipTop={tooltipTop}
        tooltipLeft={tooltipLeft}
        dimensions={dimensions}
      />
    </Box>
  );
});

export default Chart;
